export type VerificationMeta = {
  grade: 'A' | 'B'
  index: number
  source: number
  concordance: number
  freshness: number
}

export type StructuredAnswer = {
  recommendation: string
  recommendationFr: string
  results: string[]
  resultsFr: string[]
  verification: VerificationMeta
  caution?: string
  cautionFr?: string
  modules: string[]
}

export type VerticalConfig = {
  tag: string
  title: string
  subtitle: string
  questions: string[]
  answers: Record<string, StructuredAnswer>
  recommendedProducts: string[]
}

export const FAQ_QUESTIONS = [
  'How is ATNA different from a chatbot?',
  'How can ATNA help my organization do more with the same team?',
  'Which industries can use BCITI + ATNA?',
  'Can BCITI integrate with our existing systems?',
  'How long does deployment take?',
  'How does ATNA verify its answers?',
  'Can ATNA perform actions instead of only answering questions?',
  'Do you have customers in production?',
  'What business results can BCITI deliver?',
  'Can I see a live demo?',
] as const

export const FAQ_ANSWERS: Record<string, StructuredAnswer> = {
  'How is ATNA different from a chatbot?': {
    recommendation:
      'A generic chatbot searches a website and returns text, nothing more. ATNA is a verified AI assistant: it understands context, relies on a verified knowledge base, shows a verification label for every answer, can take action—open a request, launch a workflow, connect to systems—or transfer to a human expert when requested.',
    recommendationFr:
      "Un chatbot générique fouille le site et renvoie du texte, sans plus. ATNA est un assistant IA vérifié : il comprend le contexte, s'appuie sur une base de connaissances vérifiée, affiche une étiquette de vérification pour chaque réponse, peut agir (ouvrir une requête, lancer un flux de travail, se connecter aux systèmes) ou transférer à un expert humain sur demande.",
    results: [
      'Context-aware, verified answers',
      'A disclosed verification label for every response',
      'Human-authorized actions and expert handoff',
    ],
    resultsFr: [
      'Réponses vérifiées tenant compte du contexte',
      'Étiquette de vérification divulguée pour chaque réponse',
      'Actions autorisées par un humain et transfert à un expert',
    ],
    verification: {
      grade: 'A',
      index: 0.93,
      source: 0.95,
      concordance: 0.93,
      freshness: 0.91,
    },
    modules: ['ATNA'],
  },
  'How can ATNA help my organization do more with the same team?': {
    recommendation:
      'ATNA expands operational capacity by automating repetitive requests, guiding users, and handling routine interactions end to end—freeing the team for higher-value work.',
    recommendationFr:
      "ATNA accroît la capacité opérationnelle en automatisant les requêtes répétitives, en orientant les usagers et en prenant en charge les interactions courantes de bout en bout, libérant l'équipe pour des tâches à plus forte valeur.",
    results: [
      'Fewer repetitive interactions for staff',
      'Guided self-service for users',
      'More time for high-value work',
    ],
    resultsFr: [
      "Moins d'interactions répétitives pour l'équipe",
      'Libre-service guidé pour les usagers',
      'Plus de temps pour les tâches à forte valeur',
    ],
    verification: {
      grade: 'B',
      index: 0.81,
      source: 0.84,
      concordance: 0.8,
      freshness: 0.79,
    },
    modules: ['ATNA', 'BCITI Requests'],
  },
  'Which industries can use BCITI + ATNA?': {
    recommendation:
      'BCITI + ATNA are built on one verified foundation adaptable to every sector: cities, government, universities, airports, transportation, health, commerce, utilities, associations, and smart communities. The architecture stays the same; only the labels change.',
    recommendationFr:
      "BCITI + ATNA reposent sur une seule fondation vérifiée adaptable à tous les secteurs (villes, gouvernement, universités, aéroports, transport, santé, commerce, services publics, associations, communautés intelligentes) — l'architecture reste la même, seules les étiquettes changent.",
    results: [
      'One reusable verified foundation',
      'Adaptable to public and private-sector contexts',
      'Consistent architecture across industries',
    ],
    resultsFr: [
      'Une fondation vérifiée réutilisable',
      'Adaptable aux contextes publics et privés',
      'Une architecture cohérente entre les secteurs',
    ],
    verification: {
      grade: 'A',
      index: 0.9,
      source: 0.92,
      concordance: 0.89,
      freshness: 0.89,
    },
    modules: ['BCITI ID', 'ATNA'],
  },
  'Can BCITI integrate with our existing systems?': {
    recommendation:
      'Yes. BCITI is API-first, with connectors for CRM, ERP, GIS, SAP, Salesforce, payments, 311, libraries, IoT, and legacy systems.',
    recommendationFr:
      'Oui, BCITI est API-first avec des connecteurs pour CRM, ERP, SIG, SAP, Salesforce, paiements, 311, bibliothèques, IoT et systèmes hérités.',
    results: [
      'API-first integration approach',
      'Connectors for core enterprise and municipal systems',
      'A path to bring legacy systems into trusted workflows',
    ],
    resultsFr: [
      'Approche d’intégration API-first',
      'Connecteurs pour les principaux systèmes municipaux et d’entreprise',
      'Un chemin pour intégrer les systèmes hérités aux flux de confiance',
    ],
    verification: {
      grade: 'A',
      index: 0.92,
      source: 0.94,
      concordance: 0.92,
      freshness: 0.9,
    },
    modules: ['BCITI ID', 'ATNA'],
  },
  'How long does deployment take?': {
    recommendation:
      'Weeks, not months. Deployment is modular: start with a foundation layer, then expand according to the scope defined with the customer.',
    recommendationFr:
      'Des semaines, pas des mois. Déploiement modulaire — on commence par une couche de base puis on étend, selon la portée définie avec le client.',
    results: [
      'A modular deployment path',
      'Start with a focused foundation layer',
      'Expand according to jointly defined scope',
    ],
    resultsFr: [
      'Un parcours de déploiement modulaire',
      'Un départ avec une couche de base ciblée',
      'Une expansion selon une portée définie ensemble',
    ],
    verification: {
      grade: 'B',
      index: 0.78,
      source: 0.8,
      concordance: 0.77,
      freshness: 0.77,
    },
    modules: ['BCITI ID', 'ATNA'],
  },
  'How does ATNA verify its answers?': {
    recommendation:
      'Every answer is accompanied by a grade, a verification index, and its underlying factors—source reliability, concordance, and freshness—with a clear distinction between fact and estimate. A human expert remains available at all times.',
    recommendationFr:
      "Chaque réponse est accompagnée d'un grade, d'un indice de vérification et des facteurs sous-jacents (fiabilité des sources, concordance, fraîcheur), avec distinction fait/estimation. Un expert humain reste disponible en tout temps.",
    results: [
      'Grade and verification index disclosed',
      'Source reliability, concordance, and freshness visible',
      'Fact and estimate clearly distinguished',
    ],
    resultsFr: [
      'Grade et indice de vérification divulgués',
      'Fiabilité des sources, concordance et fraîcheur visibles',
      'Distinction claire entre fait et estimation',
    ],
    verification: {
      grade: 'A',
      index: 0.94,
      source: 0.96,
      concordance: 0.94,
      freshness: 0.92,
    },
    modules: ['ATNA'],
  },
  'Can ATNA perform actions instead of only answering questions?': {
    recommendation:
      'Yes, with human authorization: open requests, submit forms, schedule meetings, and launch workflows. ATNA never executes on its own.',
    recommendationFr:
      "Oui, avec autorisation humaine — ouvrir des requêtes, soumettre des formulaires, planifier des rencontres, lancer des flux de travail. ATNA n'exécute jamais de son propre chef.",
    results: [
      'Open requests and submit forms',
      'Schedule meetings and launch workflows',
      'Human authorization remains required',
    ],
    resultsFr: [
      'Ouverture de requêtes et soumission de formulaires',
      'Planification de rencontres et lancement de flux de travail',
      'Autorisation humaine toujours requise',
    ],
    verification: {
      grade: 'A',
      index: 0.91,
      source: 0.93,
      concordance: 0.91,
      freshness: 0.89,
    },
    modules: ['ATNA', 'BCITI Requests'],
  },
  'Do you have customers in production?': {
    recommendation:
      'Yes—verified digital identity has been live in Brossard since 2017, with 88,266 verified identities and approximately 78% coverage. Initial ATNA modules are in production with Casselman and Clarence-Rockland.',
    recommendationFr:
      'Oui — identité numérique vérifiée à Brossard depuis 2017 (88 266 identités vérifiées, ~78 % de couverture), et premiers modules ATNA en production avec Casselman et Clarence-Rockland.',
    results: [
      'Verified digital identity live in Brossard since 2017',
      '88,266 verified identities and approximately 78% coverage',
      'Initial ATNA modules in production',
    ],
    resultsFr: [
      'Identité numérique vérifiée en production à Brossard depuis 2017',
      '88 266 identités vérifiées et environ 78 % de couverture',
      'Premiers modules ATNA en production',
    ],
    verification: {
      grade: 'A',
      index: 0.95,
      source: 0.97,
      concordance: 0.95,
      freshness: 0.93,
    },
    caution:
      'Caution: Casselman and Clarence-Rockland references require written approval before public use.',
    cautionFr:
      'Attention : les références à Casselman et Clarence-Rockland doivent être validées par accord écrit avant tout usage public.',
    modules: ['BCITI ID', 'ATNA'],
  },
  'What business results can BCITI deliver?': {
    recommendation:
      'BCITI can deliver greater organizational capacity, lower costs, fewer calls, faster service, stronger engagement, new revenue, and accelerated digital transformation.',
    recommendationFr:
      "Capacité organisationnelle accrue, coûts réduits, moins d'appels, service plus rapide, engagement accru, nouveaux revenus, transformation numérique accélérée.",
    results: [
      'Greater organizational capacity and lower costs',
      'Fewer calls and faster service',
      'Stronger engagement and new revenue opportunities',
    ],
    resultsFr: [
      'Capacité organisationnelle accrue et coûts réduits',
      "Moins d'appels et un service plus rapide",
      'Engagement accru et nouvelles occasions de revenus',
    ],
    verification: {
      grade: 'B',
      index: 0.8,
      source: 0.83,
      concordance: 0.79,
      freshness: 0.78,
    },
    modules: ['BCITI ID', 'ATNA'],
  },
  'Can I see a live demo?': {
    recommendation:
      'Yes—a 30-minute executive demonstration tailored to your sector, with an architecture review and optional technical deep dive.',
    recommendationFr:
      'Oui — démonstration exécutive de 30 minutes adaptée au secteur, avec revue d’architecture et plongée technique en option.',
    results: [
      'A 30-minute sector-specific executive demo',
      'Architecture review included',
      'Optional technical deep dive',
    ],
    resultsFr: [
      'Une démonstration exécutive de 30 minutes adaptée au secteur',
      'Revue d’architecture incluse',
      'Plongée technique offerte en option',
    ],
    verification: {
      grade: 'A',
      index: 0.9,
      source: 0.92,
      concordance: 0.9,
      freshness: 0.88,
    },
    modules: ['ATNA'],
  },
}

const createFaqConfig = (tag: string): VerticalConfig => ({
  tag,
  title: 'Ask ATNA',
  subtitle: 'Verified intelligence built on trusted infrastructure.',
  questions: [...FAQ_QUESTIONS],
  answers: FAQ_ANSWERS,
  recommendedProducts: ['BCITI ID', 'BCITI Requests', 'ATNA'],
})

export const ATNA_CONFIG: Record<string, VerticalConfig> = {
  home: createFaqConfig('home'),
  city: createFaqConfig('city'),
  airport: createFaqConfig('airport'),
  telco: createFaqConfig('telco'),
  health: createFaqConfig('health'),
  university: createFaqConfig('university'),
  brossard: createFaqConfig('brossard'),
  other: createFaqConfig('other'),
}
